import React, { useState } from 'react';
import { X, AlertTriangle, RotateCcw, Trash2 } from 'lucide-react';
import { resetModuleProgress, resetCourseProgress, resetAllProgress, ResetModule } from '../lib/supabase';

interface ResetModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  courseId?: string;
  courseName?: string;
  showLevel3?: boolean; // Only on Profile page
  onResetComplete: () => void;
}

type ResetLevel = 'module' | 'course' | 'all';

interface ModuleOption {
  id: ResetModule;
  label: string;
  icon: string;
}

const moduleOptions: ModuleOption[] = [
  { id: 'quiz', label: 'Quiz (MC-Fragen)', icon: '📝' },
  { id: 'fill-in', label: 'Lückentext', icon: '✏️' },
  { id: 'transform', label: 'Satzumwandlung', icon: '🔄' },
  { id: 'vocab-match', label: 'Vokabel-Matching', icon: '🎯' },
  { id: 'flashcards', label: 'Lernkarten', icon: '🃏' },
  { id: 'lessons', label: 'Lernstoff', icon: '📖' },
];

export function ResetModal({
  isOpen,
  onClose,
  userId,
  courseId,
  courseName,
  showLevel3 = false,
  onResetComplete,
}: ResetModalProps) {
  const [level, setLevel] = useState<ResetLevel>('module');
  const [selectedModule, setSelectedModule] = useState<ResetModule | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [showConfirmAll, setShowConfirmAll] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleReset = async () => {
    if (isResetting) return;

    setIsResetting(true);
    let success = false;

    try {
      if (level === 'module' && selectedModule && courseId) {
        success = await resetModuleProgress(userId, courseId, selectedModule);
        if (success) {
          const moduleName = moduleOptions.find(m => m.id === selectedModule)?.label || selectedModule;
          setSuccessMessage(`${moduleName} wurde zurückgesetzt!`);
        }
      } else if (level === 'course' && courseId) {
        success = await resetCourseProgress(userId, courseId);
        if (success) {
          setSuccessMessage(`${courseName || 'Kurs'} wurde komplett zurückgesetzt!`);
        }
      } else if (level === 'all') {
        success = await resetAllProgress(userId);
        if (success) {
          setSuccessMessage('Alle Fortschritte wurden zurückgesetzt!');
        }
      }

      if (success) {
        setTimeout(() => {
          onResetComplete();
          onClose();
          // Reload page to reflect changes
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      console.error('Reset error:', error);
    } finally {
      setIsResetting(false);
    }
  };

  const canReset = () => {
    if (level === 'module') return selectedModule !== null;
    if (level === 'course') return courseId !== undefined;
    if (level === 'all') return showConfirmAll;
    return false;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '16px',
    }}>
      <div style={{
        background: '#131842',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '400px',
        maxHeight: '80vh',
        overflow: 'auto',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}>
          <h2 style={{ color: '#fff', fontSize: '18px', margin: 0 }}>
            Fortschritt zurücksetzen
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.6)',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Success message */}
        {successMessage && (
          <div style={{
            padding: '16px',
            background: 'rgba(76, 175, 80, 0.15)',
            borderBottom: '1px solid rgba(76, 175, 80, 0.3)',
            color: '#4caf50',
            textAlign: 'center',
            fontWeight: 600,
          }}>
            {successMessage}
          </div>
        )}

        {/* Level selection */}
        {!successMessage && (
          <div style={{ padding: '16px' }}>
            <div style={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: '12px',
              fontWeight: 600,
              marginBottom: '12px',
              textTransform: 'uppercase',
            }}>
              Was zurücksetzen?
            </div>

            {/* Level radio buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
              {courseId && (
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: level === 'module' ? 'rgba(255, 213, 79, 0.15)' : 'rgba(255,255,255,0.05)',
                  border: level === 'module' ? '2px solid #ffd54f' : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '10px',
                  cursor: 'pointer',
                }}>
                  <input
                    type="radio"
                    name="level"
                    checked={level === 'module'}
                    onChange={() => setLevel('module')}
                    style={{ display: 'none' }}
                  />
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    border: `2px solid ${level === 'module' ? '#ffd54f' : 'rgba(255,255,255,0.3)'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {level === 'module' && (
                      <div style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: '#ffd54f',
                      }} />
                    )}
                  </div>
                  <div>
                    <div style={{ color: '#e8eaf6', fontWeight: 600, fontSize: '14px' }}>
                      Einzelnes Modul
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                      Nur ein bestimmtes Modul zurücksetzen
                    </div>
                  </div>
                </label>
              )}

              {courseId && (
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: level === 'course' ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255,255,255,0.05)',
                  border: level === 'course' ? '2px solid #ff9800' : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '10px',
                  cursor: 'pointer',
                }}>
                  <input
                    type="radio"
                    name="level"
                    checked={level === 'course'}
                    onChange={() => setLevel('course')}
                    style={{ display: 'none' }}
                  />
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    border: `2px solid ${level === 'course' ? '#ff9800' : 'rgba(255,255,255,0.3)'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {level === 'course' && (
                      <div style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: '#ff9800',
                      }} />
                    )}
                  </div>
                  <div>
                    <div style={{ color: '#e8eaf6', fontWeight: 600, fontSize: '14px' }}>
                      Gesamter Kurs
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                      {courseName || 'Kurs'} komplett zurücksetzen
                    </div>
                  </div>
                </label>
              )}

              {showLevel3 && (
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: level === 'all' ? 'rgba(239, 83, 80, 0.15)' : 'rgba(255,255,255,0.05)',
                  border: level === 'all' ? '2px solid #ef5350' : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '10px',
                  cursor: 'pointer',
                }}>
                  <input
                    type="radio"
                    name="level"
                    checked={level === 'all'}
                    onChange={() => { setLevel('all'); setShowConfirmAll(false); }}
                    style={{ display: 'none' }}
                  />
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    border: `2px solid ${level === 'all' ? '#ef5350' : 'rgba(255,255,255,0.3)'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {level === 'all' && (
                      <div style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: '#ef5350',
                      }} />
                    )}
                  </div>
                  <div>
                    <div style={{ color: '#ef5350', fontWeight: 600, fontSize: '14px' }}>
                      Alle Fortschritte
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                      ALLE Kurse und Daten löschen
                    </div>
                  </div>
                </label>
              )}
            </div>

            {/* Module selection (Level 1) */}
            {level === 'module' && courseId && (
              <div>
                <div style={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '12px',
                  fontWeight: 600,
                  marginBottom: '12px',
                  textTransform: 'uppercase',
                }}>
                  Modul auswählen
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {moduleOptions.map((module) => (
                    <button
                      key={module.id}
                      onClick={() => setSelectedModule(module.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '12px',
                        background: selectedModule === module.id
                          ? 'rgba(255, 213, 79, 0.2)'
                          : 'rgba(255,255,255,0.03)',
                        border: selectedModule === module.id
                          ? '2px solid #ffd54f'
                          : '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <span style={{ fontSize: '20px' }}>{module.icon}</span>
                      <span style={{
                        color: selectedModule === module.id ? '#ffd54f' : '#e8eaf6',
                        fontSize: '14px',
                        fontWeight: selectedModule === module.id ? 600 : 400,
                      }}>
                        {module.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Confirmation for Level 3 */}
            {level === 'all' && (
              <div style={{
                background: 'rgba(239, 83, 80, 0.1)',
                border: '1px solid rgba(239, 83, 80, 0.3)',
                borderRadius: '12px',
                padding: '16px',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '12px',
                }}>
                  <AlertTriangle size={20} style={{ color: '#ef5350' }} />
                  <span style={{ color: '#ef5350', fontWeight: 600 }}>
                    Achtung!
                  </span>
                </div>
                <p style={{
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: '14px',
                  marginBottom: '16px',
                  lineHeight: 1.5,
                }}>
                  Das kann nicht rückgängig gemacht werden! Alle Quizze, Lernkarten,
                  Streaks und Fortschritte werden unwiderruflich gelöscht.
                </p>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={showConfirmAll}
                    onChange={(e) => setShowConfirmAll(e.target.checked)}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span style={{ color: '#e8eaf6', fontSize: '14px' }}>
                    Ja, ich bin sicher
                  </span>
                </label>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {!successMessage && (
          <div style={{
            padding: '16px',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            gap: '12px',
          }}>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: '14px',
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '10px',
                color: '#e8eaf6',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Abbrechen
            </button>
            <button
              onClick={handleReset}
              disabled={!canReset() || isResetting}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '14px',
                background: level === 'all' ? '#ef5350' : '#ff9800',
                border: 'none',
                borderRadius: '10px',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 700,
                cursor: canReset() && !isResetting ? 'pointer' : 'not-allowed',
                opacity: canReset() && !isResetting ? 1 : 0.5,
              }}
            >
              {isResetting ? (
                'Wird zurückgesetzt...'
              ) : level === 'all' ? (
                <>
                  <Trash2 size={18} />
                  Alles löschen
                </>
              ) : (
                <>
                  <RotateCcw size={18} />
                  Zurücksetzen
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
